import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/styles.css';

const DistributionBoard = () => {
    const [filterType, setFilterType] = useState('all');
    const [floor, setFloor] = useState('');
    const [searchText, setSearchText] = useState('');
    const [distributionBoards, setDistributionBoards] = useState([]);
    const [filteredBoards, setFilteredBoards] = useState([]);
    const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedBoard, setSelectedBoard] = useState(null);
    const [showLoadList, setShowLoadList] = useState(false);
    const [spareActive, setSpareActive] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    const loadListRef = useRef(null);

    // 층 데이터
    const floorOptions = [
        { id: 'B3F', name: 'B3층' },
        { id: 'B1F', name: 'B1층' },
        { id: '1F', name: '1층' },
        { id: '2F', name: '2층' },
        { id: '3F', name: '3층' },
        { id: '4F', name: '4층' },
        { id: '5F', name: '5층' },
        { id: '6F', name: '6층' },
        { id: '7F', name: '7층' },
        { id: '8F', name: '8층' },
        { id: '9F', name: '9층' },
        { id: '10F', name: '10층' },
        { id: '11F', name: '11층' },
        { id: '12F', name: '12층' },
        { id: '14F', name: '14층' },
        { id: '15F', name: '15층' },
        { id: '16F', name: '16층' },
        { id: '17F', name: '17층' },
        { id: '18F', name: '18층' },
        { id: '19F', name: '19층' },
        { id: '20F', name: '20층' },
        { id: '21F', name: '21층' },
        { id: '22F', name: '22층' },
        { id: '23F', name: '23층' },
        { id: '24F', name: '24층' },
        { id: '25F', name: '25층' },
        { id: '26F', name: '26층' },
        { id: '27F', name: '27층' },
        { id: '28F', name: '28층' },
        { id: '29F', name: '29층' },
        { id: '30F', name: '30층' },
        { id: '31F', name: '31층' },
        { id: '32F', name: '32층' },
        { id: '33F', name: '33층' },
        { id: 'M33F', name: 'M33층' },
        { id: '옥상', name: '옥상' }
    ].sort((a, b) => {
        // 특수 케이스 처리
        if (a.id === '옥상') return 1;
        if (b.id === '옥상') return -1;
        
        // 지하층 처리
        const aIsBasement = a.id.startsWith('B');
        const bIsBasement = b.id.startsWith('B');
        
        if (aIsBasement && !bIsBasement) return -1;
        if (!aIsBasement && bIsBasement) return 1;
        
        if (aIsBasement && bIsBasement) {
            // 지하층끼리 비교 (숫자가 클수록 더 아래층)
            const aNum = parseInt(a.id.replace('B', '').replace('F', ''));
            const bNum = parseInt(b.id.replace('B', '').replace('F', ''));
            return bNum - aNum; // 역순 정렬
        }
        
        // M층 처리
        const aIsM = a.id.startsWith('M');
        const bIsM = b.id.startsWith('M');
        
        if (!aIsM && bIsM) return -1;
        if (aIsM && !bIsM) return 1;
        
        // 일반 층 처리
        const aNum = parseInt(a.id.replace('F', ''));
        const bNum = parseInt(b.id.replace('F', ''));
        return aNum - bNum;
    });

    const fetchCSVData = useCallback(async () => {
        const SPREADSHEET_CSV_URL = 'https://docs.google.com/spreadsheets/d/1QHwv1YpflqE6T9AEcEbP47CRWPXKwuOJ4lFmC8RPM_M/export?format=csv';
        
        try {
            const response = await fetch(SPREADSHEET_CSV_URL);
            const text = await response.text();
            const data = parseCSV(text);

            const formattedData = data.map(row => {
                const board = {};
                Object.keys(row).forEach(header => {
                    if (header.startsWith('분기')) {
                        if (!board['분기']) board['분기'] = [];
                        if (row[header]) board['분기'].push(row[header]);
                    } else {
                        board[header] = row[header];
                    }
                });
                return board;
            });

            setDistributionBoards(formattedData);
            setFilteredBoards(formattedData);
            console.log('CSV 데이터 로드 완료:', formattedData.length, '개의 분전반 데이터');
        } catch (error) {
            console.error('CSV 데이터 가져오기 오류:', error);
        }
    }, []);

    const parseCSV = (text) => {
        const rows = text.split('\n').map(row => row.split(','));
        const headers = rows[0].map(header => header.trim());
        const data = rows.slice(1).map(row => {
            let obj = {};
            row.forEach((cell, index) => {
                if (headers[index]) {
                    obj[headers[index]] = cell.trim();
                }
            });
            return obj;
        });
        return data;
    };

    const applyFilters = useCallback(() => {
        let results = distributionBoards;

        // 전압 필터링
        if (filterType !== 'all') {
            if (filterType === 'single') {
                results = results.filter(board => board['전압'] && board['전압'].includes('1상'));
            } else if (filterType === 'three-phase-three-wire') {
                results = results.filter(board => board['전압'] && board['전압'].includes('3상3선'));
            } else if (filterType === 'three-phase-four-wire') {
                results = results.filter(board => board['전압'] && board['전압'].includes('3상4선'));
            }
        }

        // 층 필터링
        if (floor) {
            results = results.filter(board => board['층'] === floor);
            setSelectedFloorPlan(floor);
        } else {
            setSelectedFloorPlan(null);
        }

        // 검색어 필터링
        if (searchText) {
            results = results.filter(board =>
                Object.values(board).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(searchText.toLowerCase())
                ) ||
                (board['분기'] && board['분기'].some(branch => branch.toLowerCase().includes(searchText.toLowerCase())))
            );
        }

        // SPARE 필터링
        if (spareActive) {
            results = results.filter(board =>
                board['분기'] && board['분기'].some(branch => branch.match(/\bSP\b/))
            );
        }

        // 마커 클릭으로 필터링
        if (selectedLocation) {
            results = results.filter(board => board['위치'] === selectedLocation);
        }

        setFilteredBoards(results);
        setSearchPerformed(true);
    }, [filterType, floor, searchText, distributionBoards, spareActive, selectedLocation]);

    useEffect(() => {
        fetchCSVData();
    }, [fetchCSVData]);

    useEffect(() => {
        applyFilters();
    }, [filterType, floor, searchText, spareActive, selectedLocation, applyFilters]);

    const getMarkerPosition = (location) => {
        if (!location) return { x: 0, y: 0 };
        const [x, y] = location.split('-').slice(1).map(Number);
        return { x: x / 10, y: y / 10 };
    };

    const handleMarkerClick = (location) => {
        if (selectedLocation === location) {
            setSelectedLocation(null);  // 마커 선택 해제
        } else {
            setSelectedLocation(location);  // 마커 선택
        }
    };

    const openModal = (board) => {
        setSelectedBoard(board);
        setShowLoadList(false);
        setModalIsOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedBoard(null);
        setShowLoadList(false);
        document.body.style.overflow = 'auto';
    };

    const toggleLoadList = () => {
        setShowLoadList(!showLoadList);
        if (!showLoadList) {
            setTimeout(() => {
                if (loadListRef.current) {
                    loadListRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 300);
        }
    };

    // 이미지 로드 완료 핸들러
    const handleImageLoaded = () => {
        console.log(`이미지 로드 완료: ${selectedFloorPlan}`);
        setImageLoading(false);
    };
    
    // 이미지 로드 오류 핸들러
    const handleImageError = () => {
        console.error(`이미지 로드 오류: ${selectedFloorPlan}`);
        setImageLoading(false);
        setImageError(true);
    };

    // 층 변경 핸들러
    const handleFloorChange = (e) => {
        const newFloor = e.target.value;
        setFloor(newFloor);
        setImageLoading(true);
        setImageError(false);
        console.log(`층 변경: ${newFloor}로 전환`);
    };

    // 이미지 소스 가져오기 (경로 수정)
    const getImageSrc = () => {
        if (!selectedFloorPlan) return '';
        // 절대 경로 사용
        return `/images/floor-plans/${selectedFloorPlan}.jpg`;
    };

    return (
        <div className="distribution-board-container">
            <div className="filter-container bg-white shadow-md rounded-lg p-4 mb-4">
                <div className="flex flex-wrap mb-4">
                    <div className="w-full mb-4">
                        <div className="flex flex-wrap">
                            <select
                                className="flex-grow mr-2 p-2 border rounded"
                                value={floor}
                                onChange={handleFloorChange}
                            >
                                <option value="">전체 층</option>
                                {floorOptions.map(option => (
                                    <option key={option.id} value={option.id}>{option.name}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                className="flex-grow p-2 border rounded"
                                placeholder="검색어 입력"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <button
                                className="ml-2 bg-blue-500 text-white px-4 py-2 rounded"
                                onClick={applyFilters}
                            >
                                검색
                            </button>
                        </div>
                    </div>
                    <div className="w-full">
                        <div className="flex flex-wrap">
                            <button
                                className={`mr-2 mb-2 px-3 py-1 rounded-full filter-button ${filterType === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                onClick={() => setFilterType('all')}
                            >
                                전체
                            </button>
                            <button
                                className={`mr-2 mb-2 px-3 py-1 rounded-full filter-button ${filterType === 'single' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                onClick={() => setFilterType('single')}
                            >
                                단상
                            </button>
                            <button
                                className={`mr-2 mb-2 px-3 py-1 rounded-full filter-button ${filterType === 'three-phase-three-wire' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                onClick={() => setFilterType('three-phase-three-wire')}
                            >
                                3상3선
                            </button>
                            <button
                                className={`mr-2 mb-2 px-3 py-1 rounded-full filter-button ${filterType === 'three-phase-four-wire' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                onClick={() => setFilterType('three-phase-four-wire')}
                            >
                                3상4선
                            </button>
                            <button
                                className={`mr-2 mb-2 px-3 py-1 rounded-full filter-button ${spareActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                onClick={() => setSpareActive(!spareActive)}
                            >
                                SPARE
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {selectedFloorPlan && (
                <div className="floor-plan-container bg-white shadow-md rounded-lg overflow-hidden mb-4">
                    {/* 로딩 표시 */}
                    {imageLoading && (
                        <div className="loading-indicator">
                            <p>도면 이미지 로딩 중...</p>
                        </div>
                    )}
                    
                    {/* 이미지 오류 표시 */}
                    {imageError && (
                        <div className="error-message">
                            <p>이미지를 로드할 수 없습니다.</p>
                            <p>파일을 확인해 주세요: {selectedFloorPlan}.jpg</p>
                        </div>
                    )}
                    
                    {/* 도면 및 마커 */}
                    <div className="img-container relative">
                        <img 
                            src={getImageSrc()} 
                            alt={`${selectedFloorPlan} 도면`} 
                            className={`${imageLoading ? 'loading' : ''}`}
                            onLoad={handleImageLoaded}
                            onError={handleImageError}
                            style={{ 
                                display: imageLoading || imageError ? 'none' : 'block'
                            }}
                        />
                        
                        {/* 마커 표시 */}
                        {!imageLoading && !imageError && distributionBoards
                            .filter(board => 
                                board['층'] === floor && 
                                (!spareActive || (spareActive && board['분기'] && board['분기'].some(branch => branch.match(/\bSP\b/)))) && 
                                (filterType === 'all' || 
                                    (filterType === 'single' && board['전압'] && board['전압'].includes('1상')) ||
                                    (filterType === 'three-phase-three-wire' && board['전압'] && board['전압'].includes('3상3선')) ||
                                    (filterType === 'three-phase-four-wire' && board['전압'] && board['전압'].includes('3상4선'))
                                )
                            )
                            .map((board, index) => {
                                const { x, y } = getMarkerPosition(board['위치']);
                                let markerColor = 'bg-red-500'; // 기본 색상 (빨간색)

                                // SPARE 데이터 확인
                                const isSpareBoard = board['분기'] && board['분기'].some(branch => branch.match(/\bSP\b/));

                                if (spareActive && isSpareBoard) {
                                    markerColor = 'bg-green-500'; // SPARE 데이터에 해당하는 마커는 초록색
                                }

                                if (selectedLocation === board['위치']) {
                                    markerColor = 'bg-blue-500'; // 클릭한 마커는 파란색
                                }

                                return (
                                    <div
                                        key={index}
                                        className={`absolute w-2 h-2 rounded-full ${markerColor}`}
                                        style={{ left: `${x}%`, top: `${y}%` }}
                                        title={board['분전반 명칭']}
                                        onClick={() => {
                                            handleMarkerClick(board['위치']);
                                            applyFilters(); // 마커 클릭 후 필터 적용
                                        }}
                                    ></div>
                                );
                            })
                        }
                    </div>
                </div>
            )}

            {searchPerformed && filteredBoards.length > 0 && (
                <div className="search-results bg-white shadow-md rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">검색 결과 ({filteredBoards.length}건)</h3>
                    <div className="space-y-2">
                        {filteredBoards.map((board, index) => (
                            <div
                                key={index}
                                className="flex justify-between items-center p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                                onClick={() => openModal(board)}
                            >
                                <div className="w-1/2 font-medium">{board['분전반 명칭']}</div>
                                <div className="w-1/4 text-gray-600">{board['용도']}</div>
                                <div className="w-1/4 text-gray-600">{board['전압']}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {searchPerformed && filteredBoards.length === 0 && (
                <div className="no-results bg-white shadow-md rounded-lg p-4 text-center">
                    <p className="text-gray-600">검색 결과가 없습니다.</p>
                </div>
            )}

            {/* 모달 */}
            {modalIsOpen && selectedBoard && (
                <div className="distribution-overlay" onClick={closeModal}>
                    <div className="distribution-modal" onClick={e => e.stopPropagation()}>
                        {/* 모달 헤더(고정) */}
                        <div className="modal-header">
                            <h2 className="modal-title">{selectedBoard['분전반 명칭']}</h2>
                            <button onClick={closeModal} className="distribution-close-button">X</button>
                        </div>
                        
                        {/* 모달 콘텐츠(스크롤) */}
                        <div className="modal-content-scroll">
                            {selectedBoard['위치'] && (
                                <div className="img-container relative mb-4">
                                    <img 
                                        src={`/images/floor-plans/${selectedBoard['층']}.jpg`}
                                        alt={`${selectedBoard['층']} 도면`} 
                                    />
                                    <div
                                        className="absolute w-3 h-3 bg-red-500 rounded-full"
                                        style={{ 
                                            left: `${getMarkerPosition(selectedBoard['위치']).x}%`, 
                                            top: `${getMarkerPosition(selectedBoard['위치']).y}%` 
                                        }}
                                    ></div>
                                </div>
                            )}
                            
                            <table className="w-full mb-4 border-collapse">
                                <tbody>
                                    <tr className="border">
                                        <td className="font-bold p-2 border">층</td>
                                        <td className="p-2 border">{selectedBoard['층']}</td>
                                        <td className="font-bold p-2 border">설치형태</td>
                                        <td className="p-2 border">{selectedBoard['설치형태']}</td>
                                    </tr>
                                    <tr className="border">
                                        <td className="font-bold p-2 border">용도</td>
                                        <td className="p-2 border">{selectedBoard['용도']}</td>
                                        <td className="font-bold p-2 border">전압</td>
                                        <td className="p-2 border">{selectedBoard['전압']}</td>
                                    </tr>
                                    <tr className="border">
                                        <td className="font-bold p-2 border">MAIN 차단기</td>
                                        <td className="p-2 border">{selectedBoard['MAIN 차단기']}</td>
                                        <td className="font-bold p-2 border">간선 SQ</td>
                                        <td className="p-2 border">{selectedBoard['간선 SQ']}</td>
                                    </tr>
                                    <tr className="border">
                                        <td className="font-bold p-2 border">TR명</td>
                                        <td className="p-2 border">{selectedBoard['TR명']}</td>
                                        <td className="font-bold p-2 border">면수</td>
                                        <td className="p-2 border">{selectedBoard['면수']}</td>
                                    </tr>
                                    <tr className="border">
                                        <td className="font-bold p-2 border">FEEDER명</td>
                                        <td className="p-2 border">{selectedBoard['FEEDER명']}</td>
                                        <td className="font-bold p-2 border">2차분기판넬</td>
                                        <td className="p-2 border">{selectedBoard['2차분기판넬']}</td>
                                    </tr>
                                    <tr className="border">
                                        <td className="font-bold p-2 border">비고</td>
                                        <td className="p-2 border" colSpan="3">{selectedBoard['비고']}</td>
                                    </tr>
                                </tbody>
                            </table>
                            
                            {selectedBoard['분기'] && selectedBoard['분기'].length > 0 && (
                                <>
                                    <button
                                        onClick={toggleLoadList}
                                        className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
                                    >
                                        {showLoadList ? '부하 리스트 숨기기' : '부하 리스트 보기'}
                                    </button>
                                    
                                    {showLoadList && (
                                        <div className="bg-gray-100 p-4 rounded load-list" ref={loadListRef}>
                                            <h3 className="font-bold mb-2">부하 리스트</h3>
                                            <div className="grid grid-cols-2 gap-2">
                                                {selectedBoard['분기'].map((load, index) => (
                                                    <div 
                                                        key={index} 
                                                        className={`bg-white p-2 border rounded shadow-sm ${load.includes('SP') ? 'sp-highlight' : ''}`}
                                                    >
                                                        {load}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DistributionBoard;